let sharedPromise = null;
let sharedKey = '';
let callbackSequence = 0;

export class GoogleMapsLoadError extends Error {
  constructor(code) {
    super(code);
    this.name = 'GoogleMapsLoadError';
    this.code = code;
  }
}

export function loadGoogleMaps({
  apiKey,
  nonce = '',
  libraries = ['places', 'marker'],
  timeoutMs = 15_000,
  documentRef = globalThis.document,
  globalRef = globalThis,
} = {}) {
  const key = String(apiKey || '').trim();
  if (!key) return Promise.reject(new GoogleMapsLoadError('MAPS_KEY_MISSING'));
  const protocol = String(globalRef?.location?.protocol || '').toLowerCase();
  if (protocol && protocol !== 'http:' && protocol !== 'https:') {
    return Promise.reject(new GoogleMapsLoadError('MAPS_ORIGIN_UNSUPPORTED'));
  }
  if (globalRef?.google?.maps) return Promise.resolve(globalRef.google.maps);
  if (sharedPromise) {
    if (key !== sharedKey) return Promise.reject(new GoogleMapsLoadError('MAPS_KEY_CONFLICT'));
    return sharedPromise;
  }
  if (!documentRef?.createElement || !documentRef?.head?.appendChild) {
    return Promise.reject(new GoogleMapsLoadError('MAPS_ENVIRONMENT_UNAVAILABLE'));
  }

  sharedKey = key;
  sharedPromise = new Promise((resolve, reject) => {
    const callbackName = `__kmTmsGoogleMapsReady${++callbackSequence}`;
    const script = documentRef.createElement('script');
    let settled = false;
    let timer = null;
    const finish = (error = null) => {
      if (settled) return;
      settled = true;
      if (timer !== null) globalThis.clearTimeout(timer);
      try { delete globalRef[callbackName]; } catch { globalRef[callbackName] = undefined; }
      if (error) {
        sharedPromise = null;
        sharedKey = '';
        reject(error);
        return;
      }
      if (!globalRef?.google?.maps) {
        sharedPromise = null;
        sharedKey = '';
        reject(new GoogleMapsLoadError('MAPS_RESPONSE_INVALID'));
        return;
      }
      resolve(globalRef.google.maps);
    };
    const params = new URLSearchParams({
      key,
      callback: callbackName,
      loading: 'async',
      v: 'weekly',
      libraries: [...new Set(libraries.map(String).filter(Boolean))].join(','),
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.defer = true;
    script.dataset.kmTmsGoogleMaps = 'true';
    script.referrerPolicy = 'strict-origin-when-cross-origin';
    if (nonce) script.nonce = nonce;
    script.onerror = () => finish(new GoogleMapsLoadError('MAPS_SCRIPT_FAILED'));
    globalRef[callbackName] = () => finish();
    timer = globalThis.setTimeout(
      () => finish(new GoogleMapsLoadError('MAPS_LOAD_TIMEOUT')),
      Math.max(1, Number(timeoutMs) || 15_000),
    );
    documentRef.head.appendChild(script);
  });
  return sharedPromise;
}

export function resetGoogleMapsLoaderForTests() {
  sharedPromise = null;
  sharedKey = '';
  callbackSequence = 0;
}
